import moment from 'moment'
import {Avatar} from 'primereact/avatar'
import {Button} from 'primereact/button'
import {Dialog} from 'primereact/dialog'
import {SplitButton} from 'primereact/splitbutton'
import {TabPanel, TabView} from 'primereact/tabview'
import {useMemo, useState} from 'react'
import {useHistory} from 'react-router-dom'
import {SystemRole} from '../../Constants'
import {useAppConfig} from '../../context/app/AppContext'
import TranslateUtils from '../../i18n/TranslateUtils'
import {QueryKeyTypes} from '../../query-manager/QueryKeyTypes'
import {QueryKeyManager} from '../../query-manager/QueryKeysManager'
import {checkEmptyString} from '../../utils/CommonUtils'
import {useQueryParams} from '../../utils/NavigatorUtils'
import {useAuth} from '../authentication/AuthProvider'
import {getSuperAdminRole} from '../authentication/AuthUtils'
import {updateImgList} from '../detail/DetailConstant'
import CustomDetailForm from '../form-utils/CustomDetailForm'
import CustomForm from '../form-utils/CustomForm'
import {component, formLayout} from '../form/Constant'
import PageContentWrapper from '../layout/PageContentWrapper'
import {severityNotification} from '../notification/NotificationConstant'
import {showNotification} from '../notification/NotificationService'
import {patientKeys, patientOptionsList} from '../patient/PatientConstants'
import Security from './Security'

const PROFILE_PATH = 'authentication'
const QueryProfileDataKey = 'User-Profile'

const queryProfile = 'profile'
const querySecurity = 'security'

// TODO translation
const Profile = () => {
    //#region Context
    const {user, updateCurrentLoggedInUser} = useAuth()
    const {userRole} = useAppConfig()
    //#endregion

    //#region Language
    const {translateCommons, translateKeys} = TranslateUtils()
    //#endregion

    //#region URL Params
    const query = useQueryParams()
    const history = useHistory()
    //#endregion

    //#region Query
    const {profileKeys} = QueryKeyManager(QueryKeyTypes.profile, user?.id)

    const profileQueryKeys = useMemo(() => {
        return profileKeys.detail({id: user?.id})
    }, [profileKeys, user?.id])
    //#endregion

    const getCurrentTabViewIndex = useMemo(() => {
        if (query && !!query?.get('tv')) {
            switch (query.get('tv')) {
                case queryProfile:
                    return 0
                case querySecurity:
                    return 1
                default:
                    return 0
            }
        }
    }, [query])

    const [currentTabView, setCurrentTabView] = useState(
        query?.get('tv') ? getCurrentTabViewIndex : 0,
    )

    const handleOnchange = e => {
        setCurrentTabView(e.index)
        history.push(
            `/information/${user?.id}?tv=${
                e.index === 0
                    ? queryProfile
                    : e.index === 1
                    ? querySecurity
                    : ''
            }`,
        )
    }

    const handleOnSubmit = data => {
        if (
            !data.firstName ||
            data.firstName?.length < 0 ||
            !!checkEmptyString(data.firstName)
        ) {
            showNotification(
                severityNotification.error,
                'First Name is required !!',
            )
            return
        }

        const isValidDate = moment(data.dob, 'DD-MM-YYYY', true).isValid()
        if (!isValidDate) {
            showNotification(
                severityNotification.error,
                'Dob format is invalid. !!',
            )
            return
        }

        let toPostData = {
            ...data,
            active: true,
        }

        updateCurrentLoggedInUser(toPostData)

        setVisibleDialog(false)
    }

    const [visibleDialog, setVisibleDialog] = useState(false)

    const handleUpdateImage = value => {
        let toPostData = {
            ...user,
            ...value,
        }

        updateCurrentLoggedInUser(toPostData)

        setVisibleDialog(false)
        showNotification(
            severityNotification.success,
            'Avatar is updated successfully !!',
        )
    }
    const dataList = useMemo(() => {
        return [
            {
                key: 'basicInformation',
                children: [
                    {
                        key: patientKeys.title,
                        options:
                            userRole === SystemRole.USER_WITH_ROLE
                                ? patientOptionsList.hcpTitle
                                : patientOptionsList.title,
                        component: component.dropdown,
                    },
                    {
                        key: patientKeys.firstName,
                        component: component.inputText,
                    },
                    {key: patientKeys.lastName, component: component.inputText},
                    {
                        key: patientKeys.email,
                        component: component.inputText,
                        keyFilter: true,
                        placeholder: translateCommons('Field is required'),
                        defaultValue: '',
                    },
                    {
                        key: patientKeys.gender,
                        options: patientOptionsList.gender,
                        component: component.dropdown,
                    },

                    {
                        key: patientKeys.dob,
                        component: component.inputMask,
                        placeholder: translateKeys('Input date of birth'),
                    },
                ],
            },

            {
                key: 'contactInformation',
                children: [
                    {key: patientKeys.address, component: component.address},
                ],
            },
        ]
    }, [translateCommons, translateKeys, userRole])

    const items = [
        {
            label: 'Change Avatar',
            icon: 'pi pi-camera',
            command: () => {
                setVisibleDialog(true)
            },
        },
        // {
        //     label: 'Remove account',
        //     icon: 'pi pi-times',
        //     command: () => {},
        // },
    ]

    return (
        <PageContentWrapper
            className={
                'flex justify-content-between align-items-center col-12'
            }>
            <TabView
                activeIndex={currentTabView}
                onTabChange={e => {
                    handleOnchange(e)
                }}
                className="text-lg col-12 p-0">
                <TabPanel header={translateKeys('Profiles')}>
                    <div className="grid mb-3">
                        {/* Avatar */}
                        <div className="md:col-3 lg:col-2 col-12">
                            <div className="flex align-items-center justify-content-center h-full">
                                <div>
                                    <Avatar
                                        id="avatar"
                                        className={`border-circle pointer w-10rem h-10rem`}
                                        image={
                                            user?.avatar
                                                ? user.avatar
                                                : require('../../assets/images/avatar.jpg')
                                        }
                                        size="lg"
                                        shape="circle"></Avatar>
                                </div>
                            </div>
                        </div>

                        {/* Basic Info */}
                        <div className="md:col-8 lg:col-9 col-12">
                            <div className="flex flex-column justify-content-around h-full  md:align-items-start  align-items-center">
                                <div>
                                    <h2 className="font-bold mb-0">
                                        {user?.firstName || ''}{' '}
                                        {user?.lastName || ''}
                                    </h2>
                                </div>
                                <div>
                                    <p className="text-darkcyan m-0 font-bold">
                                        {user?.email || '-'}
                                    </p>
                                </div>
                                <div>
                                    <p className="font-medium text-lg  font-bold">
                                        {translateCommons('Joined at')}:{' '}
                                        {user?.createdAt
                                            ? moment(user.createdAt).format(
                                                  'DD MMM yyyy HH:mm',
                                              )
                                            : '-'}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="md:col-1 col-12 relative">
                            <div className="flex justify-content-center md:justify-content-end align-items-center">
                                <SplitButton
                                    className="p-button-rounded"
                                    label="Actions"
                                    icon="pi pi-cog"
                                    onClick={() => {}}
                                    model={items}
                                />
                            </div>
                        </div>
                    </div>
                    {/* Profile Information */}
                    <div className="col-12 p-0 m-0">
                        {!!user && !!user?.id && (
                            <CustomDetailForm
                                profile={true}
                                initialData={user}
                                QueryKey={profileQueryKeys}
                                PATH={PROFILE_PATH}
                                dataList={dataList}
                                /* Handle Function */
                                onSubmit={handleOnSubmit}
                            />
                        )}
                    </div>
                </TabPanel>
                <TabPanel header={translateKeys('Security')}>
                    {/* Change Password Screen */}
                    <div className="grid col-12 justify-content-center">
                        <Security />
                    </div>
                </TabPanel>
                {(!!getSuperAdminRole() || userRole === SystemRole.USER) && (
                    <TabPanel
                        className="p-0 mt-2"
                        style={{position: 'absolute', right: 10}}
                        headerTemplate={
                            <Button
                                icon="pi pi-arrow-left"
                                label={`${translateKeys('BACK TO HOME PAGE')}`}
                                onClick={() => {
                                    history.push('/')
                                }}
                            />
                        }
                        header={translateKeys('BACK TO HOME PAGE')}></TabPanel>
                )}
            </TabView>

            {/* Avatar Audit Form */}
            <Dialog
                header={
                    <h5 className="mb-0 font-bold">
                        <strong>{translateKeys('Change Avatar')}</strong>
                    </h5>
                }
                breakpoints={{
                    '960px': '75vw',
                    '641px': '100vw',
                }}
                style={{width: '40vw'}}
                visible={visibleDialog}
                onHide={() => setVisibleDialog(false)}>
                <CustomForm
                    onSubmit={handleUpdateImage}
                    layout={formLayout.inline}
                    dataList={updateImgList}
                />
            </Dialog>
        </PageContentWrapper>
    )
}

export default Profile
